import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, firstValueFrom, map } from 'rxjs';
import { FireService } from 'src/app/services/fire.service'; // Assuming path is correct
import { GlobalService } from 'src/app/shared/global.service'; // Assuming path is correct

@Component({
  selector: 'app-policy-approvement',
  templateUrl: './policy-approvement.component.html',
  styleUrls: ['./policy-approvement.component.scss'],
})
export class PolicyApprovementComponent implements OnInit {
  registrationForm!: FormGroup;
  public currentStep = 0;
  public progress = 0;
  loading: boolean = false;
  license: any;
  showForm: boolean = false;
  flagSelected: string = 'en';
  resultsPrivacy: any[] = [];
  videoPrivacy: any[] = [];
  inputData: any[] = [];
  isPresentPrivacy: boolean = false;
  expireDate: string;
  nameUserPrivacy: string;


  constructor(
    public fb: FormBuilder,
    public translate: TranslateService,
    private route: ActivatedRoute,
    private g: GlobalService,
    private fire: FireService,
    private router: Router
  ) {
    const browserLang = navigator.language.split('-')[0];
    this.translate.setDefaultLang(browserLang); // Set a default language


    // Initialize the registration form with validation rules
    this.registrationForm = this.fb.group({
      step1: this.fb.group({
        name: ['', [Validators.required, Validators.minLength(2)]],
        surname: ['', [Validators.required, Validators.minLength(2)]],
        birthday: ['', [Validators.required, this.ageValidator(18)]]
      }),
      step2: this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        emailConfirm: ['', [Validators.required, Validators.email]],
      }, { validators: this.emailMatchValidator }),
      step3: this.fb.group({
        company: ['', Validators.required],
        companyID: ['', Validators.required],
      }),
      step4: this.fb.group({
        privacyCheckboxes: this.fb.array([]),
      }),
      step5: this.fb.group({
        privacyCheckboxesVideo: this.fb.array([]),
      }),

    });

    this.updateProgress();
  }

  

  ngOnInit(): void {
  }

  ageValidator(minAge: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      try {
        const birthDate = new Date(control.value);
        if (isNaN(birthDate.getTime())) {
          return { invalidDate: true };
        }
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age >= minAge ? null : { ageInvalid: true };
      } catch (e) {
        return { invalidDate: true };
      }
    };
  }

  emailMatchValidator(group: AbstractControl): ValidationErrors | null {
    const email = group.get('email')?.value;
    const emailConfirm = group.get('emailConfirm')?.value;
    if (group.get('email')?.errors || group.get('emailConfirm')?.errors) {
      return null;
    }
    return email === emailConfirm ? null : { emailsDoNotMatch: true };
  }


  // --- Form Step Management ---

  stepForm(step: string): FormGroup {
    return this.registrationForm.get(step) as FormGroup;
  }

  getPrivacyCheckboxesControls(stepKey = 'step5'): AbstractControl[] {
    const formArray = this.registrationForm.get(stepKey + '.privacyCheckboxes' + (stepKey === 'step5' ? 'Video' : '')) as FormArray;
    return formArray ? formArray.controls : [];
  }

  async nextStep() {
    const privacyCheckbox = this.registrationForm.value?.['step4']?.privacyCheckboxes?.[0]?.selection;

    if (this.currentStep === 3 && !privacyCheckbox) {
      return;
    }

    const currentStepKey = `step${this.currentStep + 1}`;
    const currentGroup = this.stepForm(currentStepKey);

    currentGroup.markAllAsTouched();

    if (currentGroup.valid) {
      this.currentStep++;
      this.updateProgress();

      this.inputData[this.currentStep - 1] = this.stepForm('step' + this.currentStep).value;

      if (this.currentStep === 3) {
        await this.loadPrivacy();
      }

    } else {
      console.warn(`Step ${this.currentStep + 1} is invalid:`, currentGroup.errors);
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.updateProgress();
    }
  }

  updateProgress(): void {
    this.progress = ((this.currentStep) / 5) * 100;
  }

  async selectFlag(flag: string) {
    this.flagSelected = flag;
    this.translate.use(flag);
    this.showForm = true;
  }

  async loadPrivacy() {

    this.loading = true;
    try {
      this.resultsPrivacy = [];
      this.videoPrivacy = [];

      (this.registrationForm.get('step5.privacyCheckboxesVideo') as FormArray).clear();
      (this.registrationForm.get('step4.privacyCheckboxes') as FormArray).clear();


      let snapshot = await firstValueFrom(
        await this.fire.db.collection<any>('privacy')
          .snapshotChanges()
          .pipe(
            map(changes =>
              changes
                .map(docChange => ({ id: docChange.payload.doc.id, ...docChange.payload.doc.data() as any })) // Include document ID
                .filter(docData => docData?.attivo)
            ),
            catchError(error => {
              console.debug("Error fetching privacy documents: ", error);
              //this.g.createErrorNotification('Error', 'Failed to load privacy policies.'); // User-friendly error
              return [[]];
            })
          )
      );

      const langUpper = this.flagSelected.toUpperCase();
      snapshot = snapshot.filter(elem => elem.attivo && elem.isAllegatoPrivacy);


      this.resultsPrivacy = snapshot.filter(elem =>
        elem.titolo?.toUpperCase().includes(`${langUpper}`) && !elem.titolo?.toUpperCase().includes('VIDEO') // Check for language code in title
      );


      this.videoPrivacy = snapshot.filter(elem =>
        elem.titolo?.split(" - ")[1]?.toUpperCase()?.includes('VIDEO') &&
        elem.titolo?.toUpperCase().includes(`[${langUpper}`)
      );


      if (this.videoPrivacy.length > 0 && this.videoPrivacy[0]?.checkbox) {
        this.buildPrivacyCheckboxes(this.videoPrivacy[0], 'step5');
      } else {
        console.warn(`No video privacy checkboxes found for language: ${langUpper}`);
      }

      if (this.resultsPrivacy.length > 0 && this.resultsPrivacy[0]?.checkbox) {
        this.buildPrivacyCheckboxes(this.resultsPrivacy[0], 'step4');
      } else {
        console.warn(`No general privacy checkboxes found for language: ${langUpper}`);
      }


    } catch (error) {
      console.debug("Error loading privacy data:", error);
      //this.g.createErrorNotification('Error', 'An error occurred while loading privacy information.');
    } finally {
      this.loading = false;
    }
  }

  buildPrivacyCheckboxes(privacy, stepKey: 'step4' | 'step5') {
    let privacyData = privacy.checkbox.ITA;

    const privacyCheckboxesArray = this.registrationForm.get(stepKey + '.privacyCheckboxes' + (stepKey === 'step5' ? 'Video' : '')) as FormArray;
    if (!privacyCheckboxesArray) return;

    privacyCheckboxesArray.clear();

    privacyData = privacyData.map(item => ({
      ...item,
      privacy_id: privacy.id
    }))

    privacyData = [
      ...privacyData.filter(item => item.non_accetto === ''),
      ...privacyData.filter(item => item.non_accetto !== '')
    ].map(item => ({
      ...item,
      privacy_id: privacy.id
    }));


    privacyData.forEach(item => {
      const validators = item.richiesto ? [Validators.required] : [];

      const privacyGroup = this.fb.group({
        id: [item.id || null],
        codice: [item.codice],
        testo: [item.testo],
        accetto_label: [item.accetto],
        non_accetto_label: [item.non_accetto],
        selection: [null, validators],
        richiesto: [item.richiesto],
        privacy_id: [item.privacy_id]
      });
      privacyCheckboxesArray.push(privacyGroup);
    });
    privacyCheckboxesArray.updateValueAndValidity();
    this.stepForm(stepKey).updateValueAndValidity();
  }

  getFormattedOutput(inputData) {

    const result: any = {
      name: inputData[0]?.name,
      surname: inputData[0]?.surname,
      birthday: inputData[0]?.birthday,
      email: inputData[1]?.email,
      company: inputData[2]?.company,
      companyID: inputData[2]?.companyID,
      dateSended: new Date().toISOString()
    };

    const videoPrivacy = inputData[4]?.privacyCheckboxesVideo || [];
    const checkboxPrivacy = inputData[3]?.privacyCheckboxes || [];

    result.privacyVideo = videoPrivacy.map(item => item.privacy_id)[0];
    result.consensiVideo = videoPrivacy.map(item => ({
      [item.testo]: item.selection
    }));

    result.privacy = checkboxPrivacy.map(item => item.privacy_id)[0];
    result.consensiGenerali = checkboxPrivacy.map(item => ({
      [item.testo]: item.selection
    }));

    return result;
  }


  async onSubmit() {
    this.registrationForm.markAllAsTouched();
    if (!this.registrationForm.valid) {
      console.warn("Form is invalid:", this.registrationForm.errors);
      const translations = await firstValueFrom(this.translate.get(['policyInvalid', 'formInvalidMessage', 'checkRequiredFields']));
      let message = translations['formInvalidMessage'];
      if (this.stepForm('step4').invalid || this.stepForm('step5').invalid) {
        message = translations['checkRequiredFields'];
      }

      //this.g.createErrorNotification(translations['formInvalidTitle'], message);
      return; // Stop submission
    }


    this.loading = true;
    try {
      this.inputData[this.currentStep] = this.stepForm('step' + (this.currentStep + 1)).value;
      // Combine data from relevant steps
      let formData = this.getFormattedOutput(this.inputData);
      // Call API to submit form
      const privacySaved = await firstValueFrom(this.fire.get('policy-manager', formData.email));
      this.isPresentPrivacy = privacySaved && privacySaved.dateSended && new Date(new Date(privacySaved.dateSended).setFullYear(new Date(privacySaved.dateSended).getFullYear() + 2)) > new Date();
      if (this.isPresentPrivacy) {
        this.expireDate = new Date(new Date(privacySaved.dateSended).setFullYear(new Date(privacySaved.dateSended).getFullYear())).toLocaleDateString('it-IT');
        this.nameUserPrivacy = privacySaved.name;
      }
      else {
        await this.fire.add('policy-manager', formData, formData.email)
      }
      this.currentStep++;
      this.updateProgress();

    } catch (error) {
      console.debug("Error during submission:", error);
      const translations = await firstValueFrom(this.translate.get(['submissionErrorTitle', 'submissionErrorMessage']));
      //this.g.createErrorNotification(translations['submissionErrorTitle'], translations['submissionErrorMessage']);
    } finally {
      this.loading = false;
    }
  }


  async downloadFile(path: string | undefined) {
    if (!path) {
      console.warn("Download path is missing.");
      //this.g.createErrorNotification('Error', 'File path is missing.');
      return;
    }
    try {
      await this.g.downloadPrivacy(path, window.location.hostname);
    } catch (error) {
      console.debug("Error downloading file:", error);
      //this.g.createErrorNotification('Error', 'Failed to download file.');
    }
  }

  sanitizeInput(controlPath: string): void {
    const control = this.registrationForm.get(controlPath);
    if (control && typeof control.value === 'string') {
      const sanitizedValue = control.value.replace(/<[^>]*>/g, '');
      if (sanitizedValue !== control.value) {
        control.setValue(sanitizedValue, { emitEvent: false });
      }
    }
  }
}
